import { useEffect, useState } from 'react';
import ToggleSwitch from '../ToggleSwitch';
import type { ICustomAlertPreference } from '../../types/api-caller';
import AdminCustomAlertEditor from './AdminCustomAlertEditor';

interface AdminCustomAlertSummaryCardProps {
  alert: ICustomAlertPreference;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: (alertName: string) => void;
  onToggle: (alertName: string, enabled: boolean) => void;
  onUpdate: (previousAlertName: string, alert: ICustomAlertPreference) => void;
}

const AdminCustomAlertSummaryCard: React.FC<AdminCustomAlertSummaryCardProps> = ({
  alert,
  isEditing,
  onEdit,
  onDelete,
  onToggle,
  onUpdate,
}) => {
  const [deleteHovered, setDeleteHovered] = useState(false);
  const [editHovered, setEditHovered] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setEditHovered(false);
      setDeleteHovered(false);
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div className="py-2">
        <AdminCustomAlertEditor alert={alert} mode="edit" onSave={(updatedAlert) => onUpdate(alert.name, updatedAlert)} />
      </div>
    );
  }

  return (
    <div className="py-3 cursor-pointer group" onClick={onEdit}>
      <div className="flex flex-row justify-between items-center mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-black group-hover:text-xl transition-all duration-200">
            {alert.name || 'Unnamed Alert'}
          </h2>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditHovered(false);
              onEdit();
            }}
            onMouseEnter={() => setEditHovered(true)}
            onMouseLeave={() => setEditHovered(false)}
          >
            <img src={editHovered ? '/icons/edit-selected.svg' : '/icons/edit.svg'} alt="Edit alert" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(alert.name);
            }}
            onMouseEnter={() => setDeleteHovered(true)}
            onMouseLeave={() => setDeleteHovered(false)}
          >
            <img src={deleteHovered ? '/icons/remove_selected.svg' : '/icons/remove.svg'} alt="Delete alert" />
          </button>
          <div onClick={(e) => e.stopPropagation()}>
            <ToggleSwitch checked={alert.enabled} onChange={(checked) => onToggle(alert.name, checked)} />
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-4">
        <div className="flex-1 flex flex-col gap-1 text-sm text-gray-700">
          <div>
            <span className="font-medium">Frequency:</span> {alert.frequency === 'DAILY' ? 'Daily' : 'Weekly'}
          </div>
          <div>
            <span className="font-medium">Increase threshold:</span>{' '}
            {alert.increase_threshold != null ? `${alert.increase_threshold}%` : 'No increase threshold set'}
          </div>
          <div>
            <span className="font-medium">Decrease threshold:</span>{' '}
            {alert.decrease_threshold != null ? `${alert.decrease_threshold}%` : 'No decrease threshold set'}
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-1 text-sm text-gray-700">
          <div>
            <span className="font-medium">Exclude 'low article' companies:</span> {alert.exclude_low_articles ? 'Yes' : 'No'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomAlertSummaryCard;
